import { Scale, FileText, BookOpen, Gavel } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import lawBuildingImage from '@/assets/law-building.jpg';
import { useEffect, useState } from 'react';

// Animation component for text appearing line by line
const AnimatedParagraph = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <p
      className={`text-lg md:text-xl text-muted-foreground leading-relaxed transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </p>
  );
};

// Animation component for sections appearing
const AnimatedSection = ({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), delay);
    return () => clearTimeout(timer);
  }, [delay]);

  return (
    <div
      className={`transition-all duration-1000 ease-out ${
        isVisible
          ? 'opacity-100 transform translate-y-0'
          : 'opacity-0 transform translate-y-4'
      }`}
    >
      {children}
    </div>
  );
};

const Resources = () => {
  const navigate = useNavigate();

  return (
    <section id="resources" className="pt-32 pb-20 bg-background">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <h2 className="font-rockwell text-4xl md:text-5xl font-semibold text-primary mb-6">
          Resources & Learning
        </h2>
        <div className="max-w-3xl mx-auto mb-6">
          <AnimatedParagraph delay={200}>
            Knowledge is the foundation of freedom. Explore our comprehensive collection of educational materials designed to empower your understanding of constitutional law and legal processes.
          </AnimatedParagraph>
        </div>
        <div className="max-w-3xl mx-auto mb-12">
          <AnimatedParagraph delay={400}>
            <span className="italic">"Those who hope to be both ignorant and free are hoping for something that never has been— and never will be." <span className="not-italic font-medium">— Thomas Jefferson</span></span>
          </AnimatedParagraph>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4">
        <div className="grid md:grid-cols-3 gap-8 justify-center">
        {/* Video & Documents Library Button */}
        <AnimatedSection delay={600}>
          <div 
            className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[280px] cursor-pointer"
            onClick={() => navigate('/law-library')}
          >
            <div className="h-full flex flex-col justify-between">
              <div>
                <div className="mb-4">
                  <div className="w-12 h-1 bg-primary mb-6"></div>
                </div>
                <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                  Video & Documents Library
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed text-sm">
                  Educational resources including videos, documents, and templates to help you understand complex legal concepts.
                </p>
              </div>
              <div className="mt-auto flex justify-start">
                <Button 
                  variant="link"
                  className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 underline pointer-events-none"
                >
                  Browse Resources <Scale className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </AnimatedSection>


        {/* Legal Dictionary Button */}
        <AnimatedSection delay={800}>
          <div 
            className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[280px] cursor-pointer"
            onClick={() => navigate('/dictionary')}
          >
            <div className="h-full flex flex-col justify-between">
              <div>
                <div className="mb-4">
                  <div className="w-12 h-1 bg-primary mb-6"></div>
                </div>
                <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                  Legal Dictionary
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed text-sm">
                  Comprehensive legal dictionary with definitions and explanations of constitutional and legal terms.
                </p>
              </div>
              <div className="mt-auto flex justify-start">
                <Button 
                  variant="link"
                  className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 underline pointer-events-none"
                >
                  Browse Dictionary <BookOpen className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </AnimatedSection>

        {/* UCC Button */}
        <AnimatedSection delay={1000}>
          <div 
            className="group bg-gradient-to-br from-primary/5 to-gold/10 rounded-xl p-8 border border-gold/20 hover:border-gold/40 transition-all duration-500 ease-out hover:shadow-lg hover:shadow-gold/20 h-[280px] cursor-pointer"
            onClick={() => navigate('/ucc')}
          >
            <div className="h-full flex flex-col justify-between">
              <div>
                <div className="mb-4">
                  <div className="w-12 h-1 bg-primary mb-6"></div>
                </div>
                <h3 className="font-rockwell text-2xl font-semibold text-primary mb-4 leading-tight">
                  The Uniform Commercial Code
                </h3>
                <p className="text-muted-foreground mb-6 leading-relaxed text-sm">
                  Comprehensive guide to understanding commercial law, financing statements, and secured transactions under the UCC.
                </p>
              </div>
              <div className="mt-auto flex justify-start">
                <Button 
                  variant="link"
                  className="text-primary hover:text-gold p-0 h-auto text-sm font-medium flex items-center gap-1 underline pointer-events-none"
                >
                  Explore UCC <Gavel className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </div>
        </AnimatedSection>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4">
        <AnimatedSection delay={1400}>
          <div className="mt-12 p-6 rounded-lg bg-muted/50 border border-gold/10">
          <p className="text-muted-foreground leading-relaxed">
            All resources are provided free of charge to support your journey toward legal independence and constitutional understanding. These materials represent our commitment to returning knowledge to the people.
          </p>
          </div>
        </AnimatedSection>
      </div>
    </section>
  );
};

export default Resources;